import Phaser from "phaser";
import type { TabViewContract, TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import type { Rect } from "@yjsgames/phaser-ui/panel-content";
import {
  SegmentedTabBar,
  SEGMENTED_TAB_BAR_EVENT,
  type TabBarStyleToken,
} from "@yjsgames/phaser-ui";
import type { TextDirection } from "@yjsgames/i18n";
import { SingleSelection } from "@yjsgames/single-select";
import { FONT_FAMILY, TYPO } from "./uiTokens.js";
import { PALETTE } from "../themes/palette.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { ShopTabView } from "./ShopTabView.js";
import type { ShopData, ShopTabViewOptions } from "./ShopTabTypes.js";
import {
  DailyShopTabView,
  type DailyShopTabData,
  type DailyShopTabViewOptions,
} from "./DailyShopTabView.js";

/** 상점 탭 내 서브탭 — 기본 상점(regular) / 일일상점(daily). */
type SubTabId = "regular" | "daily";

/** 서브탭 바 높이 + 콘텐츠와의 간격. 콘텐츠를 서브탭 바에 바짝 붙여 영역 확보(사용자 요청). */
const SUB_BAR_HEIGHT = 30;
const SUB_BAR_GAP = 2;

/** 서브탭 바 스타일 — 하단 탭바와 동일 9-slice 에셋, caption 폰트. */
const SUB_TAB_BAR_STYLE: TabBarStyleToken = {
  activeTextureKey: "tab_button_active_9slice",
  inactiveTextureKey: "tab_button_inactive_9slice",
  border: { left: 10, top: 10, right: 10, bottom: 10 },
  labelStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textSecondary },
  labelActiveStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textBright },
  inactiveAlpha: 0.65,
  activeLabelScale: 1.04,
};

function iconTab(activeIconKey: string): { iconKey: string; activeIconKey: string; inactiveIconKey: string } {
  return { iconKey: activeIconKey, activeIconKey, inactiveIconKey: `${activeIconKey}_inactive` };
}

export interface ShopContainerOptions {
  readonly shop: ShopTabViewOptions;
  readonly dailyShop: DailyShopTabViewOptions;
  /** 일일상점 서브탭 활성 여부(featureFlags.dailyShop). false면 상점만 표시(서브탭 바 없음). */
  readonly dailyShopEnabled: boolean;
  /** 서브탭 전환 시 호출 — GameScene이 활성 서브탭 데이터를 갱신한다. */
  readonly onSubChange: () => void;
}

/**
 * 상점 컨테이너 탭 — 하단 서브탭 바([상점]/[일일상점], 엄지 친화) + 활성 서브탭 콘텐츠 1개를 표시한다.
 * 기존 별도 최상위 탭이던 shop/dailyShop을 한 탭으로 통합(사용자 요청). 가시성은 SingleSelection SSoT.
 *
 * featureFlags.dailyShop=false(웹 live)면 서브탭 바 없이 ShopTabView만 풀 영역으로 둔다 —
 * dailyShop 게이트가 "탭 제거"에서 "서브탭 제거"로 의미 전환된 결과.
 * setVisible/relayout/applyLanguage는 자식 뷰(+서브탭 바)에 위임한다.
 */
export class ShopContainerTabView implements TabViewContract {
  private readonly subBar?: SegmentedTabBar<SubTabId>;
  private readonly shopView: ShopTabView;
  private readonly dailyView?: DailyShopTabView;
  private readonly selection: SingleSelection<SubTabId>;
  private readonly onSubChange: () => void;
  /** 컨테이너 탭 자체 표시 상태 — 자식 가시성은 (this.visible && 서브탭 활성)으로 게이트. */
  private visible = true;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    bottomY: number,
    opts: ShopContainerOptions,
  ) {
    this.onSubChange = opts.onSubChange;
    const dir = getTextDirection();
    const hasDaily = opts.dailyShopEnabled;
    // 엄지 친화(thumb-friendly): 서브탭 바를 콘텐츠 영역 하단에 배치 — 모바일에서 엄지로 닿기 쉬운 위치.
    // 콘텐츠는 상단(y)부터 시작하고, 일일상점 6슬롯 그리드 하한을 서브탭 바 위로 좁힌다.
    const subBarY = bottomY - SUB_BAR_HEIGHT;
    const contentBottomY = hasDaily ? subBarY - SUB_BAR_GAP : bottomY;

    this.shopView = new ShopTabView(scene, x, y, width, contentBottomY, opts.shop);
    if (hasDaily) {
      this.dailyView = new DailyShopTabView(scene, x, y, width, contentBottomY, opts.dailyShop);
      this.subBar = new SegmentedTabBar<SubTabId>(scene, x, subBarY, {
        style: SUB_TAB_BAR_STYLE,
        width,
        height: SUB_BAR_HEIGHT,
        tabs: [
          { id: "regular", label: t("tab.shop"), ...iconTab("icon_subtab_shop") },
          { id: "daily", label: t("tab.dailyShop"), ...iconTab("icon_subtab_daily_shop") },
        ],
        activeId: "regular",
        dir,
      });
      this.subBar.setDepth(3);
      this.subBar.on(SEGMENTED_TAB_BAR_EVENT.TabChange, (id: SubTabId) => this.selectSub(id));
      scene.add.existing(this.subBar);
    }
    this.selection = new SingleSelection<SubTabId>(
      hasDaily ? ["regular", "daily"] : ["regular"],
      "regular",
    );
    this.applySubVisibility();
  }

  private selectSub(id: SubTabId): void {
    if (!this.selection.select(id)) return;
    this.subBar?.setActiveTab(id);
    this.applySubVisibility();
    this.onSubChange();
  }

  /** 서브탭 가시성을 selection SSoT에서 파생 — 컨테이너가 보일 때만 활성 서브탭 1개를 표시. */
  private applySubVisibility(): void {
    const vis = this.selection.visibility();
    this.shopView.setVisible(this.visible && vis.regular);
    this.dailyView?.setVisible(this.visible && (vis.daily ?? false));
  }

  /** 현재 활성 서브탭 — GameScene이 어느 데이터(상점/일일상점)를 갱신할지 결정. */
  activeSub(): SubTabId {
    return this.selection.active;
  }

  updateShop(d: ShopData): void {
    this.shopView.update(d);
  }

  updateDaily(d: DailyShopTabData): void {
    this.dailyView?.update(d);
  }

  /** 가이드퀘스트 하이라이트용 — 기본 상점 서브탭의 골드 상자 구매 버튼 rect. */
  goldBoxButtonRect(): Rect | null {
    if (this.selection.active !== "regular") return null;
    return this.shopView.goldBoxButtonRect();
  }

  applyLanguage(): void {
    this.subBar?.setTabLabels((id) => (id === "regular" ? t("tab.shop") : t("tab.dailyShop")));
    this.shopView.applyLanguage();
    this.dailyView?.applyLanguage();
  }

  relayout(dir: TextDirection): void {
    this.subBar?.relayout(dir);
    this.shopView.relayout(dir);
    this.dailyView?.relayout(dir);
  }

  /** 셸 펼치기/접기 — 새 콘텐츠 rect를 상점/일일상점 서브뷰에 전달(서브탭 바는 패널 하단 고정이라 daily는 자체 bottomY 유지). */
  resize(rect: TabContentRect): void {
    this.shopView.resize(rect);
    this.dailyView?.resize(rect);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.subBar?.setVisible(visible);
    this.applySubVisibility();
  }
}
